import { useRouter } from 'next/router';

import { DisplayTypeEnum, QueryParamsFields } from '../../types';

/**
 * Custom React hook that determines whether a component should render
 * based on the current URL query parameters.
 *
 * @param fields - Configuration object defining:
 *   - `queryParamKey`: The query parameter key to inspect.
 *   - `queryParamValue`: The expected value to match for the given key.
 *   - `queryParamAction`: The action to take when a match occurs
 *      (e.g., show when matched or hide when matched).
 *
 * @returns A boolean indicating if the component should render.
 *
 * The hook reads the current URL (excluding the hash), checks for the
 * configured query parameter and value, and applies the specified action
 * to decide whether the component should be rendered. If the router is
 * not ready or the configuration is incomplete, it defaults to rendering.
 */

export const useShouldRenderForQueryParam = ({
  queryParamKey,
  queryParamValue,
  queryParamAction,
}: QueryParamsFields): boolean => {
  const router = useRouter();
  const key = queryParamKey?.value?.trim();
  const value = queryParamValue?.value?.trim();
  const action = queryParamAction?.name;
  if (!key || !value || !action) return true;

  if (!router.isReady) return true;
  const asPathWithoutHash = router.asPath.split('#')[0];
  const search = asPathWithoutHash.split('?')[1];
  if (!search) return action === DisplayTypeEnum.hide;

  const params = new URLSearchParams(search);
  const values = params.getAll(key);
  const shouldRender = values.includes(value);

  return action === DisplayTypeEnum.show ? shouldRender : !shouldRender;
};
